import { Params } from '@angular/router';

import { Dayjs } from 'dayjs';

import { isString, SDate, SDuration, SEnumTypeMapping } from '@summit/core';

import { Duration } from 'dayjs/plugin/duration';
import {
  FinancialFormatter,
  FuelFormatter,
  MoneyFormatter,
  Number2PlacesFormatter,
  NumberFormatter,
  PercentageFormatter,
  RateFormatter,
  TimeFormatter
} from '../string-functions';
import { DisplayType } from './display-type.enum';
import { EquationTypes } from './equation-types.enum';
import { IDisplaySchema } from './i-display-schema';
import { ListFooter } from './list-footer';
import { SummitIdObject } from './summit-id-object';
import { DateRange } from './time/date-range';
import { Week } from './time/week';
import { ViewField } from './view/view-field';

const debugging = false;

export class SummitDisplay {
  public static getAutoTextLabel<T>(
    element: Partial<T>,
    attribute: Partial<ViewField<T>> | undefined
  ): string | number | undefined {
    if (!element) return undefined;
    if (attribute === null || attribute === undefined) {
      return 'Error';
    }
    // if (debugging)
    //   console.log(
    //     `[getAutoText] call using property ${attribute?.propertyName.toString()}`,
    //     element[attribute.propertyName]
    //   );
    let actualValue: unknown = undefined;
    if (attribute.valueGetterOverride) actualValue = attribute.valueGetterOverride({ data: element });
    if (actualValue === undefined && attribute.propertyName) actualValue = element[attribute.propertyName];
    const response = SummitDisplay.formatAutoTextLabel(actualValue, attribute);
    //if (debugging) console.log(`[getAutoText] - ${attribute?.propertyName.toString()} - returning modified`, response);
    return response || response == 0 ? response : attribute.displayNullText;
  }

  public static formatAutoTextLabel<T>(field: unknown, attribute: Partial<ViewField<T>>): string | number | undefined {
    if (debugging)
      console.log(`[formatAutoTextLabel] - ${attribute?.propertyName?.toString()} - called with`, field, attribute);
    let response: string | number | undefined;
    if (!attribute) {
      return 'Invalid Attribute';
    }
    switch (attribute?.displayType) {
      case DisplayType.Array:
        if (field) {
          response = (field as Array<unknown>)
            .map((entry: unknown) => entry)
            .filter((entry: unknown) => entry !== null)
            .join('\n');
        }
        break;
      case DisplayType.Boolean:
        //not sure why we need to test for match to null text - MaintEventEdit
        if ((field === null || field === attribute.displayNullText) && attribute.displayNullText) response = undefined;
        else response = field && field !== 'No' ? 'Yes' : 'No';
        //console.log(`${attribute.propertyName} Boolean`, field, response);
        break;
      case DisplayType.CalendarDateUtc:
        response = field ? (field as Dayjs).format('MM/DD') : undefined;
        break;
      case DisplayType.Custom:
        response = `Unknown Custom Field Type for attribute ${attribute.propertyName as string}`;
        break;
      case DisplayType.DateLocal:
        if (isString(field)) return field;
        response = field ? (field as Dayjs).format('M/D/YY') : undefined;
        break;
      case DisplayType.DateUtc:
        if (isString(field)) return field;
        response = field ? (field as Dayjs).format('M/D/YY') : undefined;
        break;
        // if (isString(field) && dayjs(field).isValid()) {
        //   if (dayjs(field).isValid()) {
        //     const day = SDate.newUtc(field);
        //     response = day.format('M/D/YY');
        //   } else {
        //     return field;
        //   }
        // }
        // if (SDate.isDayjs(field)) {
        //   response = field.format('M/D/YY');
        // } else if (!response) {
        //   //console.log('DateUtc', field, response);
        //   response = field ? field.format('M/D/YY') : null;
        // }
        break;
      case DisplayType.DateTimeLocal:
        if (isString(field)) return field;
        response = field ? (field as Dayjs).local().format('M/D/YY HH:mm') : undefined;
        break;
      case DisplayType.DateTimeSecLocal:
        if (isString(field)) return field;
        response = field ? (field as Dayjs).local().format('M/D/YY HH:mm:ss') : undefined;
        break;
      case DisplayType.DateTimeUtc:
        if (isString(field)) return field;
        response = field ? (field as Dayjs).format('M/D/YY HH:mm') : undefined;
        break;
      case DisplayType.DateTimeSecUtc:
        if (isString(field)) return field;
        response = field ? (field as Dayjs).format('M/D/YYYY HH:mm:ss') : undefined; //full year to allow copy past into datetime field
        break;
      case DisplayType.FormattedText:
        response = field?.toString();
        break;
      case DisplayType.ScheduleTime:
        {
          let format = 'dd h:mm a';
          const timeValue = field as Dayjs;
          if (timeValue?.second() == 1) format = 'dddd';
          response = field ? timeValue.format(format) : undefined;
        }
        break;
      case DisplayType.Time:
        if (field) {
          if (SDate.isDayjs(field)) response = field.format('HH:mm');
          else response = TimeFormatter(field as Duration, 12);
        }
        break;
      case DisplayType.TimeUtc:
        if (field) {
          if (SDate.isDayjs(field)) response = field.format('HH:mm');
          else response = TimeFormatter(field as Duration, 12);
        }
        break;
      case DisplayType.Duration:
        if (field) {
          const lclValue = SDuration.isDuration(field) ? field : SDuration.new(field, false);
          response = `${Math.trunc(lclValue.asHours())}:${NumberFormatter(lclValue.minutes(), 2)}`;
        }
        break;
      case DisplayType.Enum:
        if (attribute?.displayTypeParameter) {
          //console.log(`Looking up ${field} in `, attribute.displayTypeParameter);
          response = (attribute.displayTypeParameter as SEnumTypeMapping)[field as number];
          //console.log(`Response = ${response}`);
        } else {
          response = field || field == 0 ? (field as number) : undefined;
        }
        break;
      case DisplayType.EnumArray:
        if (attribute?.displayTypeParameter) {
          //console.log(`Looking up ${field} in `, attribute.displayTypeParameter);
          response = Array.isArray(field)
            ? field.map((f) => (attribute.displayTypeParameter as SEnumTypeMapping)[f as number]).join(', ')
            : undefined;
          //console.log(`Response = ${response}`);
        } else {
          response = Array.isArray(field) ? field.join(', ') : undefined;
        }
        break;
      case DisplayType.Fuel:
        response = SummitDisplay.IsValid(field) ? FuelFormatter(field as number) : undefined;
        break;
      case DisplayType.Miles:
      case DisplayType.Number:
        //console.log(`Miles/Number for  ${attribute.propertyName} = `, field);
        response = SummitDisplay.IsValid(field) ? NumberFormatter(field as number) : undefined;
        if (attribute.displayNullText != undefined && response === '0') {
          response = attribute.displayNullText;
        }
        //console.log(`Miles/Number for  ${attribute.propertyName} = ${response}`);
        break;
      case DisplayType.Money:
        response = SummitDisplay.IsValid(field) ? MoneyFormatter(field as number) : undefined;
        break;
      case DisplayType.Financial:
        response = SummitDisplay.IsValid(field) ? FinancialFormatter(field as number) : undefined;
        break;
      case DisplayType.Percentage:
        response = SummitDisplay.IsValid(field) ? PercentageFormatter(field as number) : undefined;
        break;
      case DisplayType.PercentageFromWholeNumber:
        response = SummitDisplay.IsValid(field) ? PercentageFormatter((field as number) / 100) : undefined;
        break;
      case DisplayType.Name:
        //console.log(`Name for  ${attribute.propertyName} = `, field);
        response = field ? (field as { name: string }).name : undefined;
        //console.log(`Name for  ${attribute.propertyName} = ${response}`);
        break;
      case DisplayType.Rate:
        response = SummitDisplay.IsValid(field) ? RateFormatter(field as number) : undefined;
        // console.log(`${field}: Using ${response}`);
        break;
      case DisplayType.MPG:
        response = SummitDisplay.IsValid(field) ? Number2PlacesFormatter(field as number) : undefined;
        break;
      case DisplayType.Digits3:
        response = SummitDisplay.IsValid(field) ? Number2PlacesFormatter(field as number, 3) : undefined;
        break;
      case DisplayType.TimeLength:
        if (SDuration.isDuration(field)) {
          response = SummitDisplay.IsValid(field) ? Number2PlacesFormatter(field.asHours()) : undefined;
        } else {
          response = SummitDisplay.IsValid(field) ? Number2PlacesFormatter(field as number) : undefined;
        }
        break;
      case DisplayType.ShortString:
        response = (field as { toShortString(): string })?.toShortString
          ? (field as { toShortString(): string }).toShortString()
          : undefined;
        break;
      case DisplayType.ShortStringArray:
      case DisplayType.ShortStringArrayFT:
        if (Array.isArray(field)) {
          response = field
            .map((entry) => (entry?.toShortString ? entry.toShortString() : entry))
            .filter((entry) => entry !== null)
            .join(', \n');
        } else {
          response = undefined;
        }
        break;
      case DisplayType.ShortStringArrayWithParameter:
      case DisplayType.ShortStringArrayWithParameterFT:
        if (Array.isArray(field)) {
          response = field
            .filter((entry) => entry?.toShortString)
            .map((entry) => entry?.toShortString(attribute.displayTypeParameter))
            .filter((entry) => entry !== null)
            .join(', \n');
        } else {
          response = undefined;
        }
        break;
      default:
        response = field || field == 0 ? (field as string) : undefined;
        //console.log(`${attribute.propertyName} = ${field} becomes ${response}`, field);
        break;
    }
    // if (attribute.propertyName === 'id' && response == 0) { console.log(`${attribute.propertyName} = ${response || response == 0} ? ${response} : ${attribute.displayNullText}`) }
    if (debugging)
      console.log(`[formatAutoTextLabel] - ${attribute?.propertyName?.toString()} - returning modified:`, response);
    return response || response == 0 ? response : attribute.displayNullText;
  }

  private static IsValid(item: unknown): boolean {
    return item != undefined && item != null;
  }

  public static getAutoTextLabelLong<T extends SummitIdObject>(
    element: T,
    attribute: ViewField<T>,
    schema: IDisplaySchema<T>
  ): string | number | undefined {
    let response: string | number | undefined;
    switch (attribute?.displayType) {
      case DisplayType.DateLocal:
      case DisplayType.DateTimeLocal:
        if (element[attribute.propertyName]) {
          const value = element[attribute.propertyName] as unknown as Dayjs;
          response = value ? value.local().format('M/D/YY dddd') : undefined;
          const now = SDate.new();
          if (value.isSame(now.subtract(1, 'day'), 'day')) {
            response += ' (Yesterday)';
          } else if (value.isSame(now, 'day')) {
            response += ' (Today)';
          } else if (value.isSame(now.add(1, 'day'), 'day')) {
            response += ' (Tomorrow)';
          }
        } else {
          response = undefined;
        }
        break;
      case DisplayType.DateUtc:
      case DisplayType.DateTimeUtc:
        if (element[attribute.propertyName]) {
          const value = element[attribute.propertyName] as unknown as Dayjs;
          response = value ? value.format('M/D/YY dddd') : undefined;
          const nowUtc = SDate.getNormalized();
          const yesterdayUtc = SDate.newUtc(nowUtc).subtract(1, 'day');
          if (value.isSame(yesterdayUtc, 'day')) {
            response += ' (Yesterday)';
          } else if (value.isSame(nowUtc, 'day')) {
            response += ' (Today)';
          } else if (value.isSame(SDate.getNormalized().add(1, 'day'), 'day')) {
            response += ' (Tomorrow)';
          }
        } else {
          response = undefined;
        }
        break;
      default:
        // console.log(`Processing ${propertyName}`, schema);
        if (schema.getTextLabel) {
          response = schema.getTextLabel(element, attribute);
        } else {
          response = this.getAutoTextLabel(element, attribute);
        }
        break;
    }
    return response ? response : attribute.displayNullText;
  }

  public static getFooterTextLabel<T extends SummitIdObject>(
    data: Array<T>,
    attribute: ViewField<T>,
    configObject: IDisplaySchema<T>
  ): string | number | string[][] | undefined {
    let response: string | number | any[][] | undefined;

    let totalDictionary: Map<any, any>;
    let objectDictionary: Map<any, any>;
    let valueField: keyof T;
    let valueAttribute: ViewField<T> | undefined;
    let displayGroupByAttribute: ViewField<T> | undefined;

    //console.log(`Getting footer text label for ${attribute.propertyName}`, data, attribute);
    if (attribute.footerEquation) {
      switch (attribute.footerEquation) {
        case EquationTypes.Sum:
          //console.log(`summing entries`, data);
          if (data) {
            let key: keyof T;
            if (attribute.footerEquationValues && attribute.footerEquationValues[0]) {
              key = attribute.footerEquationValues[0];
            } else {
              key = attribute.propertyName;
            }
            const total = data.reduce((a, b) => a + ((b[key] as unknown as number) ?? 0), 0);
            //console.log(`got ${total} from ${key}`);
            response = SummitDisplay.formatAutoTextLabel(total, attribute);
            //console.log(`returning ${response} from ${key}`);
          }
          break;
        case EquationTypes.SumBy:
        case EquationTypes.SumByAndDisplayBy: {
          if (!attribute.footerEquationValues) {
            break;
          }
          totalDictionary = new Map();
          objectDictionary = new Map();
          let byIndex = 0;
          let groupIndex = 0;
          //View Field selection
          if (attribute.footerEquation == EquationTypes.SumBy) {
            /*if (attribute.propertyName == 'routeShiftName')
              console.log(`${attribute.propertyName}: Processing as SumBy`);*/
            if (attribute.footerEquationValues.length == 1) {
              valueField = attribute.propertyName;
            } else {
              valueField = attribute.footerEquationValues[0];
              byIndex = 1;
              groupIndex = 1;
            }
          } else {
            /*if (attribute.propertyName == 'routeShiftName')
            console.log(`${attribute.propertyName}: Processing as SumBy And Display by`);*/
            if (attribute.footerEquationValues.length == 2) {
              valueField = attribute.propertyName;
              byIndex = 0;
              groupIndex = 1;
            } else {
              valueField = attribute.footerEquationValues[0];
              byIndex = 1;
              groupIndex = 2;
            }
          }
          if (valueField === attribute.propertyName) {
            valueAttribute = attribute;
          } else {
            valueAttribute = configObject.getAttribute(valueField);
          }
          if (!valueAttribute) {
            console.error(`Could not find attributes for:`, attribute.footerEquationValues);
          }
          const byField = attribute.footerEquationValues[byIndex];
          displayGroupByAttribute = configObject.getAttribute(attribute.footerEquationValues[groupIndex]);
          if (data) {
            data
              .filter((entry) => entry)
              .forEach((entry) => {
                const label = entry[byField];
                //console.log(`Checking to see if ${byField}: ${label} is in`, totalDictionary);
                if (totalDictionary.has(label)) {
                  //console.log(`Adding ${entry[valueField] ?? 0} to ${totalDictionary[label]} for ${label}`);
                  totalDictionary.set(label, totalDictionary.get(label) + (entry[valueField] ?? 0));
                } else {
                  if (label || entry[valueField]) {
                    //console.log(`Creating ${entry[valueField]} for ${label}`);
                    totalDictionary.set(label, entry[valueField] ?? 0);
                    objectDictionary.set(label, entry);
                  }
                }
              });
          }
          if (valueAttribute !== undefined) {
            const tempArray = Array.from(totalDictionary.keys())
              .map((key) => [
                configObject.getTextLabel(objectDictionary.get(key), displayGroupByAttribute),
                SummitDisplay.formatAutoTextLabel(totalDictionary.get(key), valueAttribute!)
              ])
              .filter((entry) => entry[1]);
            response = tempArray.sort((a: Array<any>, b: Array<any>) => {
              if (a[0] > b[0]) return 1;
              if (a[0] < b[0]) return -1;
              return 0;
            });
          }
          break;
        }
        case EquationTypes.PercentageOf:
          // console.log(`Percentage Calc`, data);
          if (data) {
            const portionKey = attribute.footerEquationValues?.[0];
            const totalKey = attribute.footerEquationValues?.[1];
            if (portionKey === undefined || totalKey === undefined) break;
            // console.log(`Percentage Calc ${portionKey} of ${totalKey}`);
            const portionTotal = data.reduce((a, b) => a + ((b[portionKey] as unknown as number) ?? 0), 0);
            const totalTotal = data.reduce((a, b) => a + ((b[totalKey] as unknown as number) ?? 0), 0);
            // console.log(`got ${portionTotal} of ${totalTotal} in `);
            response = SummitDisplay.formatAutoTextLabel(portionTotal / totalTotal, attribute);
            // console.log(`got ${this.displayValue} from ${key} in `, newData);
          }
          break;
        case EquationTypes.DividedBy:
          // console.log(`Division Calc`, data);
          if (data) {
            const numeratorKey = attribute.footerEquationValues?.[0];
            const denominatorKey = attribute.footerEquationValues?.[1];
            if (numeratorKey === undefined || denominatorKey === undefined) break;
            //console.log(`Division Calc ${numeratorKey} of ${denominatorKey}`);
            const numeratorTotal = data.reduce((a, b) => a + ((b[numeratorKey] as unknown as number) ?? 0), 0);
            const denominatorTotal = data.reduce((a, b) => a + ((b[denominatorKey] as unknown as number) ?? 0), 0);
            //console.log(`got ${numeratorTotal} of ${denominatorTotal}`);
            response = SummitDisplay.formatAutoTextLabel(numeratorTotal / denominatorTotal, attribute);
            // console.log(`got ${this.displayValue} from ${key} in `, newData);
          }
          break;
        case EquationTypes.First:
          console.log(`First`, data);
          var reducedArray =
            data && data.length > 0
              ? data
                  .map((entry) => entry[attribute.propertyName])
                  .filter((entry) => entry !== undefined && entry !== null && entry !== '')
              : [];
          if (reducedArray.length > 0) {
            response = SummitDisplay.formatAutoTextLabel(reducedArray[0], attribute);
          } else {
            response = undefined;
          }
          break;
        case EquationTypes.Custom:
          break;
        default:
          console.error(`Unknown Equation Type for Footer of ${attribute.footerEquation}`);
          break;
      }
    }
    return response;
  }

  public static getGroupFooterClass<T>(groupRow: ListFooter<T>): string {
    let response: string;
    if (!(groupRow.groupDepth >= 1)) {
      response = 'groupByFooter';
    } else {
      response = 'groupByFooter' + groupRow.groupDepth.toString();
    }
    return response;
  }

  public static getQueryParams<T>(element: T, attribute: ViewField<T>): Params {
    const response = new Object() as Params;
    attribute.queryParams
      ?.sort((a, b) => (a[0] > b[0] ? -1 : 1)) //Alpha sort to align with view collection
      .forEach((entry) => {
        switch (entry[0]) {
          case 'WeekOf': {
            let dateStringVar: string;
            if (entry[1].startsWith('this.')) {
              dateStringVar = entry[1].replace('this.', '');
            } else {
              dateStringVar = entry[1];
            }
            const selWeek = new Week(element[dateStringVar as keyof T] as any);
            // console.log(`Searching on ${element[dateStringVar]} = ${selWeek.start.format('MM/DD/YYYY')}=>${selWeek.end.format('MM/DD/YYYY')}`, selWeek);
            response['WeekRange'] = JSON.stringify({
              filterData: {
                begin: selWeek.start,
                end: selWeek.end
              }
            });
            break;
          }
          case 'WeekRange':
            switch (entry[1]) {
              case '[PayrollWeek]':
                response['WeekRange'] = JSON.stringify({
                  filterData: new DateRange({
                    newWeekType: 'PayrollWeek'
                  })
                });
                break;
              case '[ReportWeek]':
                response['WeekRange'] = JSON.stringify({
                  filterData: new DateRange({
                    newWeekType: 'ReportWeek'
                  })
                });
                break;
              default:
                response['WeekRange'] = JSON.stringify({
                  filterData: new DateRange()
                });
            }
            break;
          default:
            if (entry[1].startsWith('this.')) {
              const variableName = entry[1].replace('this.', '');
              if ((element as any)[variableName] || (element as any)[variableName] === 0) {
                response[entry[0]] = (element as any)[variableName];
              }
            } else {
              response[entry[0]] = entry[1];
            }
            break;
        }
      });
    return response;
  }
}
